import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle, Check, CheckCircle2, Loader2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { DEFAULT_STATUSES } from '../../../lib/constants';
import { statusName, statusValueName } from '../../../lib/statusName';

// `onPick({status, statusId})` is the preferred callback — fires once per
// selection so callers can persist both fields in a single update (and a
// single undo entry). `onChange` / `onStatusIdChange` remain as a fallback
// for call sites that maintain status + status_id as separate local state
// (e.g. UnifiedTaskCreation).
const StatusPicker = ({ value, statusId, customStatuses = [], onPick, onChange, onStatusIdChange, children, disabled = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const getStatusDisplay = () => {
    if (statusId && customStatuses.length > 0) {
      const custom = customStatuses.find(s => s.id === statusId);
      if (custom) return { label: statusName(custom, t), color: custom.color || '#6B7280' };
    }
    return { label: statusValueName(DEFAULT_STATUSES[value] ? value : 'todo', t), color: value === 'completed' ? '#22C55E' : value === 'in_progress' ? '#3B82F6' : '#6B7280' };
  };

  const display = getStatusDisplay();

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent/60'}`}
      style={{ color: display.color }}
    >
      <Circle className="w-3.5 h-3.5" style={{ color: display.color }} />
      <span>{display.label}</span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        {customStatuses.length > 0 ? (
          customStatuses.map(status => (
            <button
              key={status.id}
              onClick={() => {
                const nextStatus = status.maps_to || 'todo';
                if (onPick) {
                  onPick({ status: nextStatus, statusId: status.id });
                } else {
                  onStatusIdChange?.(status.id);
                  onChange?.(nextStatus);
                }
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${statusId === status.id ? 'bg-accent/60' : ''}`}
            >
              <div className="flex items-center gap-2">
                <Circle className="w-3.5 h-3.5" style={{ color: status.color }} />
                <span className="text-foreground/80">{statusName(status, t)}</span>
              </div>
              {statusId === status.id && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />}
            </button>
          ))
        ) : (
          Object.keys(DEFAULT_STATUSES).map((key) => (
            <button
              key={key}
              onClick={() => {
                if (onPick) {
                  onPick({ status: key, statusId: null });
                } else {
                  onChange?.(key);
                }
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${value === key ? 'bg-accent/60' : ''}`}
            >
              <div className="flex items-center gap-2">
                {key === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> :
                 key === 'in_progress' ? <Loader2 className="w-3.5 h-3.5 text-blue-400" /> :
                 <Circle className="w-3.5 h-3.5 text-muted-foreground" />}
                <span className="text-foreground/80">{statusValueName(key, t)}</span>
              </div>
              {value === key && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

export default StatusPicker;
